import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PlatformAdminSideNav } from './PlatformAdminSideNav';
import { PlatformAdminTopBar } from './PlatformAdminTopBar';
import { usePlatformAdminAuth } from '../contexts/PlatformAdminAuthContext';
import { ScrollToTop } from '../../../components/ScrollToTop.js';

/**
 * Platform Admin Layout Component
 * 
 * Main layout for platform admin portal with:
 * - Top navigation bar with AstraPeak Platform Admin branding
 * - Side navigation with platform admin sections
 * - Main content area
 * 
 * Different from tenant layout:
 * - No tenant context
 * - Platform admin branding
 * - System-wide navigation
 */

interface PlatformAdminLayoutProps {
  children: React.ReactNode;
}

export const PlatformAdminLayout: React.FC<PlatformAdminLayoutProps> = ({ children }) => {
  const [isSideNavOpen, setIsSideNavOpen] = useState(true);
  const { platformAdmin } = usePlatformAdminAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-[#0F172A]">
      {/* Top Navigation Bar */}
      <PlatformAdminTopBar 
        platformAdmin={platformAdmin}
        onMenuClick={() => setIsSideNavOpen(!isSideNavOpen)}
      />

      <div className="flex h-[calc(100vh-64px)] mt-16">
        {/* Mobile Overlay - Only shows on mobile/tablet (lg:hidden) when sidebar is open */}
        {isSideNavOpen && (
          <div 
            className="fixed inset-0 top-16 bg-black/50 z-30 lg:hidden"
            onClick={() => setIsSideNavOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Side Navigation */}
        <PlatformAdminSideNav isOpen={isSideNavOpen} />

        {/* Main Content */}
        <main className={`
          flex-1 overflow-auto transition-all duration-300 ease-in-out
          ${isSideNavOpen ? 'lg:ml-64' : 'ml-0'}
        `}>
          <ScrollToTop />
          {children}
        </main>
      </div>
    </div>
  );
};

export default PlatformAdminLayout;
